import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  Briefcase, Plus, X, Eye, FileText, Clock, MapPin, Users,
  ChevronDown, ChevronUp, Mail, Phone, Calendar, CheckCircle2,
  XCircle, MessageSquare, Edit, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JOB_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'permanent', label: 'Permanent' },
  { value: 'trainee', label: 'Trainee / Supernumerary' },
];

const APP_STATUSES = [
  { value: 'submitted', label: 'Submitted', color: '#4A90E2' },
  { value: 'reviewed', label: 'Reviewed', color: '#F97316' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: '#00A6A0' },
  { value: 'offered', label: 'Offered', color: '#A8E6CF' },
  { value: 'rejected', label: 'Rejected', color: '#FF6F61' },
];

export function ManageJobPostings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('postings');
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingPosting, setEditingPosting] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [selectedPostingFilter, setSelectedPostingFilter] = useState('');

  const [postingForm, setPostingForm] = useState({
    title: '', description: '', job_type: 'full_time', location: '',
    salary_range: '', requirements: '', benefits: '',
    experience_years: '', certifications_required: ''
  });

  useEffect(() => {
    fetchPostings();
    fetchApplications();
  }, []);

  const fetchPostings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/jobs/postings?org_id=${user?.organization_id || ''}`, { withCredentials: true });
      setPostings(data);
    } catch (err) {
      console.error('Failed to fetch postings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/jobs/applications`, { withCredentials: true });
      setApplications(data);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
  };

  const handleCreatePosting = async (e) => {
    e.preventDefault();
    if (!postingForm.title) { toast.error('Title is required'); return; }
    try {
      const payload = {
        ...postingForm,
        requirements: postingForm.requirements ? postingForm.requirements.split('\n').filter(Boolean) : [],
        benefits: postingForm.benefits ? postingForm.benefits.split('\n').filter(Boolean) : [],
        certifications_required: postingForm.certifications_required ? postingForm.certifications_required.split(',').map(s => s.trim()).filter(Boolean) : [],
        experience_years: postingForm.experience_years ? parseInt(postingForm.experience_years) : null,
      };
      if (editingPosting) {
        await axios.put(`${API_URL}/api/jobs/postings/${editingPosting}`, payload, { withCredentials: true });
        toast.success('Job posting updated');
      } else {
        await axios.post(`${API_URL}/api/jobs/postings`, payload, { withCredentials: true });
        toast.success('Job posting created');
      }
      setShowCreateDialog(false);
      setEditingPosting(null);
      resetForm();
      fetchPostings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save job posting');
    }
  };

  const handleClosePosting = async (postingId) => {
    try {
      await axios.delete(`${API_URL}/api/jobs/postings/${postingId}`, { withCredentials: true });
      toast.success('Job posting closed');
      fetchPostings();
    } catch (err) {
      toast.error('Failed to close posting');
    }
  };

  const handleEditPosting = (posting) => {
    setPostingForm({
      title: posting.title || '',
      description: posting.description || '',
      job_type: posting.job_type || 'full_time',
      location: posting.location || '',
      salary_range: posting.salary_range || '',
      requirements: (posting.requirements || []).join('\n'),
      benefits: (posting.benefits || []).join('\n'),
      experience_years: posting.experience_years?.toString() || '',
      certifications_required: (posting.certifications_required || []).join(', '),
    });
    setEditingPosting(posting.id);
    setShowCreateDialog(true);
  };

  const handleUpdateAppStatus = async (appId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/jobs/applications/${appId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Application marked as ${newStatus.replace('_', ' ')}`);
      fetchApplications();
    } catch (err) {
      toast.error('Failed to update application status');
    }
  };

  const resetForm = () => {
    setPostingForm({
      title: '', description: '', job_type: 'full_time', location: '',
      salary_range: '', requirements: '', benefits: '',
      experience_years: '', certifications_required: ''
    });
  };

  const activePostings = postings.filter(p => p.status === 'active');
  const closedPostings = postings.filter(p => p.status === 'closed');

  const filteredApps = selectedPostingFilter
    ? applications.filter(a => a.posting_id === selectedPostingFilter)
    : applications;

  const tabs = [
    { id: 'postings', label: 'Job Postings', count: activePostings.length },
    { id: 'applications', label: 'Applications', count: applications.length },
  ];

  const typeColors = {
    full_time: '#00A6A0', part_time: '#4A90E2', contract: '#FF6F61',
    permanent: '#A8E6CF', trainee: '#F97316',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="manage-job-postings">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Job Postings
            </h1>
            <p className="text-muted-foreground mt-1">
              Create listings, manage applications, and hire caregivers
            </p>
          </div>
          <Button className="btn-primary" onClick={() => { resetForm(); setEditingPosting(null); setShowCreateDialog(true); }} data-testid="create-posting-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Job Posting
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-border/50 pb-2">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={`${activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}`}
              onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
              {t.label}
              {t.count > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-muted">{t.count}</span>
              )}
            </Button>
          ))}
        </div>

        {/* JOB POSTINGS TAB */}
        {activeTab === 'postings' && (
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-12"><div className="spinner mx-auto" /></div>
            ) : activePostings.length === 0 && closedPostings.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Job Postings Yet</h3>
                  <p className="text-muted-foreground mb-6">Create your first job posting to start attracting caregivers</p>
                  <Button className="btn-primary" onClick={() => { resetForm(); setShowCreateDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />Create First Posting
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {activePostings.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Active ({activePostings.length})</h3>
                    {activePostings.map(posting => (
                      <Card key={posting.id} className="glass-card" data-testid={`posting-${posting.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1">
                              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${typeColors[posting.job_type] || '#94A3B8'}15` }}>
                                <Briefcase className="w-5 h-5" style={{ color: typeColors[posting.job_type] || '#94A3B8' }} />
                              </div>
                              <div>
                                <h3 className="font-heading font-semibold text-foreground">{posting.title}</h3>
                                <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-muted-foreground">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                                    style={{ backgroundColor: `${typeColors[posting.job_type] || '#94A3B8'}15`, color: typeColors[posting.job_type] || '#94A3B8' }}>
                                    <Clock className="w-3 h-3" />{JOB_TYPES.find(t => t.value === posting.job_type)?.label || posting.job_type}
                                  </span>
                                  {posting.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{posting.location}</span>}
                                  {posting.salary_range && <span className="text-[#00A6A0]">{posting.salary_range}</span>}
                                </div>
                                <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{posting.views_count || 0} views</span>
                                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{posting.applications_count || 0} applications</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditPosting(posting)} data-testid={`edit-posting-${posting.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleClosePosting(posting.id)} data-testid={`close-posting-${posting.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                {closedPostings.length > 0 && (
                  <div className="space-y-3 mt-6">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Closed ({closedPostings.length})</h3>
                    {closedPostings.map(posting => (
                      <Card key={posting.id} className="glass-card opacity-60" data-testid={`posting-closed-${posting.id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center gap-4">
                            <Briefcase className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <h3 className="font-heading font-medium text-foreground">{posting.title}</h3>
                              <p className="text-xs text-muted-foreground">{posting.location} &middot; {posting.applications_count || 0} applications</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* APPLICATIONS TAB */}
        {activeTab === 'applications' && (
          <div className="space-y-4">
            {/* Filter by posting */}
            {postings.length > 0 && (
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Filter by posting:</Label>
                <select
                  value={selectedPostingFilter}
                  onChange={(e) => setSelectedPostingFilter(e.target.value)}
                  className="rounded-md border border-border bg-card text-foreground px-3 py-1.5 text-sm"
                  data-testid="filter-posting-select"
                >
                  <option value="">All postings</option>
                  {postings.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
            )}

            {filteredApps.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Applications Yet</h3>
                  <p className="text-muted-foreground">Applications will appear here when candidates apply to your job postings</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredApps.map(app => {
                  const isExpanded = expandedApp === app.id;
                  const statusInfo = APP_STATUSES.find(s => s.value === app.status) || APP_STATUSES[0];
                  return (
                    <Card key={app.id} className="glass-card" data-testid={`application-${app.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4 flex-1">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${statusInfo.color}15` }}>
                              <span className="font-heading font-bold text-sm" style={{ color: statusInfo.color }}>
                                {(app.applicant_name || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-heading font-semibold text-foreground">{app.applicant_name || 'Unknown'}</h3>
                              <p className="text-sm text-muted-foreground">{app.applicant_email}</p>
                              <div className="flex items-center gap-3 mt-1.5">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                                  style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }}>
                                  {statusInfo.label}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {app.created_at ? new Date(app.created_at).toLocaleDateString() : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setExpandedApp(isExpanded ? null : app.id)}>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-border space-y-4 animate-in slide-in-from-top-1">
                            {app.cover_letter && (
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                                  <FileText className="w-3.5 h-3.5" />Cover Letter
                                </h4>
                                <p className="text-sm text-muted-foreground whitespace-pre-line bg-muted/50 rounded-lg p-3">{app.cover_letter}</p>
                              </div>
                            )}
                            {app.experience && (
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-1">Experience</h4>
                                <p className="text-sm text-muted-foreground">{app.experience}</p>
                              </div>
                            )}
                            {app.availability && (
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5" />Availability
                                </h4>
                                <p className="text-sm text-muted-foreground">{app.availability}</p>
                              </div>
                            )}
                            {app.cv_filename && (
                              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                                <FileText className="w-4 h-4 text-[#00A6A0]" />
                                <span className="text-sm text-foreground">{app.cv_filename}</span>
                              </div>
                            )}
                            {app.documents?.length > 0 && (
                              <div className="space-y-1">
                                <h4 className="text-sm font-semibold text-foreground">Documents</h4>
                                {app.documents.map((doc, i) => (
                                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                                    <FileText className="w-4 h-4 text-[#4A90E2]" />
                                    <span className="text-sm text-foreground">{doc.filename}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Status Actions */}
                            <div>
                              <h4 className="text-sm font-semibold text-foreground mb-2">Update Status</h4>
                              <div className="flex flex-wrap gap-2">
                                {APP_STATUSES.map(s => (
                                  <Button
                                    key={s.value}
                                    variant={app.status === s.value ? 'default' : 'outline'}
                                    size="sm"
                                    className={app.status === s.value ? '' : 'text-muted-foreground'}
                                    style={app.status === s.value ? { backgroundColor: s.color, color: '#fff' } : {}}
                                    onClick={() => handleUpdateAppStatus(app.id, s.value)}
                                    data-testid={`status-${s.value}-btn`}
                                  >
                                    {s.label}
                                  </Button>
                                ))}
                              </div>
                            </div>

                            {/* Contact Actions */}
                            <div className="flex gap-2">
                              {app.applicant_email && (
                                <a href={`mailto:${app.applicant_email}`}>
                                  <Button variant="outline" size="sm" data-testid="contact-email-btn">
                                    <Mail className="w-3.5 h-3.5 mr-1.5" />Email
                                  </Button>
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Job Posting Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) { setEditingPosting(null); resetForm(); } }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-foreground">
              {editingPosting ? 'Edit Job Posting' : 'Create Job Posting'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePosting} className="space-y-4 py-2">
            <div>
              <Label className="text-foreground">Job Title *</Label>
              <Input value={postingForm.title} onChange={(e) => setPostingForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Live-in Caregiver" className="bg-background border-border mt-1" required data-testid="posting-title-input" />
            </div>
            <div>
              <Label className="text-foreground">Job Type</Label>
              <select value={postingForm.job_type} onChange={(e) => setPostingForm(p => ({ ...p, job_type: e.target.value }))}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm" data-testid="posting-type-select">
                {JOB_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-foreground">Location</Label>
              <Input value={postingForm.location} onChange={(e) => setPostingForm(p => ({ ...p, location: e.target.value }))}
                placeholder="e.g. London, Manchester" className="bg-background border-border mt-1" data-testid="posting-location-input" />
            </div>
            <div>
              <Label className="text-foreground">Salary Range</Label>
              <Input value={postingForm.salary_range} onChange={(e) => setPostingForm(p => ({ ...p, salary_range: e.target.value }))}
                placeholder="e.g. £25,000 - £30,000" className="bg-background border-border mt-1" data-testid="posting-salary-input" />
            </div>
            <div>
              <Label className="text-foreground">Description</Label>
              <textarea value={postingForm.description} onChange={(e) => setPostingForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe the role, responsibilities..." rows={4}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm resize-none" data-testid="posting-description-input" />
            </div>
            <div>
              <Label className="text-foreground">Requirements <span className="text-muted-foreground text-xs">(one per line)</span></Label>
              <textarea value={postingForm.requirements} onChange={(e) => setPostingForm(p => ({ ...p, requirements: e.target.value }))}
                placeholder="NVQ Level 2 in Health &amp; Social Care&#10;Minimum 1 year experience&#10;Valid DBS check" rows={3}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm resize-none" data-testid="posting-requirements-input" />
            </div>
            <div>
              <Label className="text-foreground">Benefits <span className="text-muted-foreground text-xs">(one per line)</span></Label>
              <textarea value={postingForm.benefits} onChange={(e) => setPostingForm(p => ({ ...p, benefits: e.target.value }))}
                placeholder="Competitive salary&#10;Flexible hours&#10;Training provided" rows={3}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm resize-none" data-testid="posting-benefits-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-foreground">Experience (years)</Label>
                <Input type="number" value={postingForm.experience_years} onChange={(e) => setPostingForm(p => ({ ...p, experience_years: e.target.value }))}
                  placeholder="1" className="bg-background border-border mt-1" data-testid="posting-experience-input" />
              </div>
              <div>
                <Label className="text-foreground">Certifications</Label>
                <Input value={postingForm.certifications_required} onChange={(e) => setPostingForm(p => ({ ...p, certifications_required: e.target.value }))}
                  placeholder="NVQ, DBS" className="bg-background border-border mt-1" data-testid="posting-certs-input" />
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary py-5" data-testid="save-posting-btn">
              {editingPosting ? 'Update Posting' : 'Publish Job Posting'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default ManageJobPostings;
